/** Browser-only file library for the client My Files page. */
import { createId, formatFileSize } from './myPatients';

export { formatFileSize };

/** ponytail: file bytes stay in this tab; metadata is what survives refresh. IndexedDB is the upgrade when uploads must outlive a reload. */
const FILE_BLOBS = new Map();
const LISTENERS = new Set();
const STORAGE_PREFIX = 'client.myFiles.v1';

export const MAX_FILE_BYTES = 15 * 1024 * 1024;

export const FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  CASE_LIBRARY_VPF: 'case_library_vpf',
  CUSTOM_VPF: 'custom_vpf',
  ECG: 'ecg',
  OTHER: 'other',
};

export const FILE_TYPE_LABELS = {
  [FILE_TYPES.IMAGE]: 'Image',
  [FILE_TYPES.VIDEO]: 'Video',
  [FILE_TYPES.CASE_LIBRARY_VPF]: 'Case Library VPF',
  [FILE_TYPES.CUSTOM_VPF]: 'Custom VPF',
  [FILE_TYPES.ECG]: 'ECG',
  [FILE_TYPES.OTHER]: 'Other',
};

export const FILE_TYPE_VALUES = Object.values(FILE_TYPES);

export const ANATOMAGE_GROUP = 'Anatomage Imaging';
export const CUSTOM_GROUP = 'Custom Imaging';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
const VIDEO_EXTENSIONS = ['mp4'];

function asText(value) {
  return value == null ? '' : String(value);
}

export function storageKey(userId) {
  return `${STORAGE_PREFIX}:${userId || 'unknown'}`;
}

export function subscribeMyFiles(listener) {
  LISTENERS.add(listener);
  return () => LISTENERS.delete(listener);
}

function notifyMyFiles(userId) {
  LISTENERS.forEach((listener) => listener(userId));
}

export function rememberFileBlob(id, file) {
  if (!id) return;
  if (file) FILE_BLOBS.set(id, file);
  else FILE_BLOBS.delete(id);
}

export function readFileBlob(id) {
  return FILE_BLOBS.get(id) || null;
}

export function clearFileBlobs() {
  FILE_BLOBS.clear();
}

export function defaultFileTag(filename) {
  const name = asText(filename);
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.slice(0, lastDot) : name;
}

export function inferFileType(filename) {
  const name = asText(filename).toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (IMAGE_EXTENSIONS.includes(ext)) return FILE_TYPES.IMAGE;
  if (VIDEO_EXTENSIONS.includes(ext)) return FILE_TYPES.VIDEO;
  if (ext === 'vpf') return FILE_TYPES.CASE_LIBRARY_VPF;
  if (ext === 'csv') return FILE_TYPES.ECG;
  return FILE_TYPES.OTHER;
}

export function extensionError(fileType, filename) {
  const name = asText(filename).toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (fileType === FILE_TYPES.IMAGE && !IMAGE_EXTENSIONS.includes(ext)) {
    return 'Photos must be PNG, JPEG, or JPG';
  }
  if (fileType === FILE_TYPES.VIDEO && !VIDEO_EXTENSIONS.includes(ext)) {
    return 'Videos must be MP4';
  }
  return '';
}

function normalizeFolder(raw, ids) {
  if (!raw || typeof raw !== 'object') return null;
  const id = asText(raw.id).trim();
  const name = asText(raw.name).trim();
  if (!id || !name || ids.has(id)) return null;
  ids.add(id);
  return {
    id,
    name,
    parentId: asText(raw.parentId).trim(),
    description: asText(raw.description),
  };
}

function normalizeFile(raw, ids) {
  if (!raw || typeof raw !== 'object') return null;
  const id = asText(raw.id).trim();
  const fileTag = asText(raw.fileTag).trim();
  if (!id || !fileTag || ids.has(id)) return null;
  ids.add(id);
  const fileType = FILE_TYPE_VALUES.includes(raw.fileType) ? raw.fileType : FILE_TYPES.OTHER;
  const fileSize = Number(raw.fileSize);
  return {
    id,
    name: asText(raw.name || raw.originalName).trim() || fileTag,
    originalName: asText(raw.originalName || raw.name).trim() || fileTag,
    fileTag,
    folderId: asText(raw.folderId).trim(),
    fileType,
    fileSize: Number.isFinite(fileSize) && fileSize > 0 ? fileSize : 0,
    customVpfSourcePath: asText(raw.customVpfSourcePath),
    uploadedAt: asText(raw.uploadedAt),
  };
}

export function normalizeLibrary(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const folderIds = new Set();
  const folders = (Array.isArray(source.folders) ? source.folders : [])
    .map((folder) => normalizeFolder(folder, folderIds))
    .filter(Boolean)
    .map((folder) => ({
      ...folder,
      parentId: folder.parentId && folderIds.has(folder.parentId) ? folder.parentId : '',
    }));
  const parentIds = new Set(folders.map((folder) => folder.id));
  const fileIds = new Set();
  const files = (Array.isArray(source.files) ? source.files : [])
    .map((file) => normalizeFile(file, fileIds))
    .filter(Boolean)
    .map((file) => ({
      ...file,
      folderId: file.folderId && parentIds.has(file.folderId) ? file.folderId : '',
    }));
  return { folders, files };
}

export function loadLibrary(storage, userId) {
  if (!storage) return normalizeLibrary(null);
  try {
    const raw = storage.getItem(storageKey(userId));
    if (!raw) return normalizeLibrary(null);
    return normalizeLibrary(JSON.parse(raw));
  } catch {
    return normalizeLibrary(null);
  }
}

export function saveLibrary(storage, userId, library) {
  const payload = normalizeLibrary(library);
  storage.setItem(storageKey(userId), JSON.stringify(payload));
  notifyMyFiles(userId);
  return payload;
}

function folderIndex(folders) {
  return new Map(folders.map((folder) => [folder.id, folder]));
}

export function myFilesHomePath() {
  return '/my-files';
}

export function myFilesFolderPath(folderId) {
  const id = asText(folderId).trim();
  if (!id) return myFilesHomePath();
  return `${myFilesHomePath()}/${encodeURIComponent(id)}`;
}

export function resolveLibraryFolderId(folders, folderId) {
  const id = asText(folderId).trim();
  if (!id) return '';
  return folderIndex(folders).has(id) ? id : null;
}

export function folderPath(folders, folderId) {
  if (!folderId) return '/';
  const byId = folderIndex(folders);
  const names = [];
  let current = byId.get(folderId) || null;
  const seen = new Set();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    const parentId = current.parentId;
    current = parentId ? byId.get(parentId) || null : null;
  }
  return `/${names.join('/')}`;
}

export function folderBreadcrumbs(folders, folderId) {
  const byId = folderIndex(folders);
  const chain = [];
  let current = folderId ? byId.get(folderId) || null : null;
  const seen = new Set();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.unshift({ id: current.id, name: current.name });
    const parentId = current.parentId;
    current = parentId ? byId.get(parentId) || null : null;
  }
  return [{ id: '', name: 'Home' }, ...chain];
}

function subtreeIds(folders, rootId) {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (let index = 0; index < folders.length; index += 1) {
      const folder = folders[index];
      if (folder.parentId && ids.has(folder.parentId) && !ids.has(folder.id)) {
        ids.add(folder.id);
        grew = true;
      }
    }
  }
  return ids;
}

export function browseLibrary(library, folderId, query) {
  const record = normalizeLibrary(library);
  const currentId = asText(folderId).trim();
  const q = asText(query).trim().toLowerCase();
  const scope = q && currentId ? subtreeIds(record.folders, currentId) : null;

  const folders = record.folders
    .filter((folder) => {
      if (!q) return folder.parentId === currentId;
      if (folder.id === currentId) return false;
      if (scope && !scope.has(folder.id)) return false;
      const hay = `${folder.name} ${folder.description}`.toLowerCase();
      return hay.includes(q);
    })
    .map((folder) => ({
      type: 'folder',
      id: folder.id,
      displayName: folder.name,
      description: folder.description,
      secondary: q ? folderPath(record.folders, folder.id) : (folder.description || 'Folder'),
    }));

  const files = record.files
    .filter((file) => {
      if (!q) return file.folderId === currentId;
      if (scope && !scope.has(file.folderId)) return false;
      const hay = `${file.fileTag} ${file.originalName} ${file.name}`.toLowerCase();
      return hay.includes(q);
    })
    .map((file) => ({
      type: 'file',
      id: file.id,
      displayName: file.fileTag,
      fileType: file.fileType,
      fileSize: file.fileSize,
      originalName: file.originalName,
      secondary: q
        ? `${formatFileSize(file.fileSize)} · ${folderPath(record.folders, file.folderId)}`
        : formatFileSize(file.fileSize),
    }));

  return [...folders, ...files];
}

function tagTaken(files, tag, exceptId) {
  const key = tag.trim().toLowerCase();
  return files.some((file) => file.id !== exceptId && file.fileTag.toLowerCase() === key);
}

export function createFolder(library, { name, description, parentId }) {
  const record = normalizeLibrary(library);
  const trimmed = asText(name).trim();
  if (!trimmed) return { library: record, error: 'Folder name is required.' };
  const parent = asText(parentId).trim();
  const clash = record.folders.some((folder) => (
    folder.parentId === parent && folder.name.toLowerCase() === trimmed.toLowerCase()
  ));
  if (clash) return { library: record, error: 'A folder with that name already exists here.' };
  const folder = {
    id: createId(),
    name: trimmed,
    parentId: parent,
    description: asText(description).trim(),
  };
  return { library: { ...record, folders: [...record.folders, folder] }, error: '', folder };
}

export function renameFolder(library, folderId, { name, description }) {
  const record = normalizeLibrary(library);
  const folder = record.folders.find((item) => item.id === folderId);
  if (!folder) return { library: record, error: 'Folder not found.' };
  const trimmed = asText(name).trim();
  if (!trimmed) return { library: record, error: 'Folder name is required.' };
  const clash = record.folders.some((item) => (
    item.id !== folder.id
    && item.parentId === folder.parentId
    && item.name.toLowerCase() === trimmed.toLowerCase()
  ));
  if (clash) return { library: record, error: 'A folder with that name already exists here.' };
  return {
    error: '',
    library: {
      ...record,
      folders: record.folders.map((item) => (
        item.id === folder.id
          ? { ...item, name: trimmed, description: asText(description).trim() }
          : item
      )),
    },
  };
}

export function renameFileTag(library, fileId, fileTag) {
  const record = normalizeLibrary(library);
  const file = record.files.find((item) => item.id === fileId);
  if (!file) return { library: record, error: 'File not found.' };
  const trimmed = asText(fileTag).trim();
  if (!trimmed) return { library: record, error: 'File tag is required.' };
  if (tagTaken(record.files, trimmed, file.id)) {
    return { library: record, error: 'That file tag is already used.' };
  }
  return {
    error: '',
    library: {
      ...record,
      files: record.files.map((item) => (
        item.id === file.id ? { ...item, fileTag: trimmed } : item
      )),
    },
  };
}

export function validateUploads(library, items) {
  const record = normalizeLibrary(library);
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return 'Choose at least one file.';
  const seen = new Set();
  for (const item of list) {
    const tag = asText(item?.fileTag).trim();
    const originalName = asText(item?.originalName).trim();
    if (!tag) return 'Every file must have a file tag.';
    const key = tag.toLowerCase();
    if (seen.has(key) || tagTaken(record.files, tag)) {
      return `File tag "${tag}" is already used.`;
    }
    seen.add(key);
    if (!FILE_TYPE_VALUES.includes(item?.fileType)) {
      return `Choose a file type for "${originalName || tag}".`;
    }
    if (item.fileType === FILE_TYPES.CUSTOM_VPF && !asText(item.customVpfSourcePath).trim()) {
      return `Source file path is required for Custom VPF: "${originalName || tag}".`;
    }
    const ext = extensionError(item.fileType, originalName);
    if (ext) return `${ext}: "${originalName || tag}".`;
    const size = Number(item?.fileSize) || 0;
    if (size > MAX_FILE_BYTES) return `"${originalName || tag}" exceeds 15 MB.`;
  }
  return '';
}

export function addUploadedFiles(library, items, folderId) {
  const record = normalizeLibrary(library);
  const error = validateUploads(record, items);
  if (error) return { library: record, error, files: [] };
  const parent = asText(folderId).trim();
  const added = items.map((item) => ({
    id: createId(),
    name: asText(item.originalName).trim(),
    originalName: asText(item.originalName).trim(),
    fileTag: asText(item.fileTag).trim(),
    folderId: parent,
    fileType: item.fileType,
    fileSize: Number(item.fileSize) || 0,
    customVpfSourcePath: asText(item.customVpfSourcePath).trim(),
    uploadedAt: new Date().toISOString(),
  }));
  return {
    error: '',
    files: added,
    library: { ...record, files: [...record.files, ...added] },
  };
}

export function removeEntry(library, entry) {
  const record = normalizeLibrary(library);
  if (!entry) return record;
  if (entry.type === 'file') {
    return { ...record, files: record.files.filter((file) => file.id !== entry.id) };
  }
  const ids = subtreeIds(record.folders, entry.id);
  return {
    folders: record.folders.filter((folder) => !ids.has(folder.id)),
    files: record.files.filter((file) => !ids.has(file.folderId)),
  };
}

export function buildFileOptions(anatomageFiles, library) {
  const anatomage = (Array.isArray(anatomageFiles) ? anatomageFiles : [])
    .map((file) => ({
      id: file?.id,
      tag: asText(file?.tag || file?.file_tag).trim(),
      name: asText(file?.name).trim(),
      source: 'anatomage',
      group: ANATOMAGE_GROUP,
    }))
    .filter((file) => file.tag && file.id != null && asText(file.id) !== '');
  const custom = normalizeLibrary(library).files.map((file) => ({
    id: file.id,
    tag: file.fileTag,
    name: file.originalName,
    source: 'custom',
    group: CUSTOM_GROUP,
  }));
  const byTag = (a, b) => a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' });
  return [...anatomage.sort(byTag), ...custom.sort(byTag)];
}

export function fileOptionsWithSelection(options, selected) {
  const list = Array.isArray(options) ? options : [];
  if (!selected) return list;
  const exists = list.some((option) => (
    option.source === selected.source && String(option.id) === String(selected.id)
  ));
  if (exists) return list;
  return [...list, selected].sort((a, b) => {
    if (a.group !== b.group) return a.group === ANATOMAGE_GROUP ? -1 : 1;
    return a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' });
  });
}

export function resolveFileOption(row, options) {
  const list = Array.isArray(options) ? options : [];
  const fileId = asText(row?.fileId).trim();
  const fileTag = asText(row?.fileTag).trim();
  const source = row?.fileSource === 'anatomage' || row?.fileSource === 'custom' ? row.fileSource : '';
  if (fileId) {
    const byId = list.find((option) => (
      String(option.id) === fileId && (!source || option.source === source)
    ));
    if (byId) return byId;
  }
  if (fileTag) {
    const byTag = list.find((option) => option.tag === fileTag && (!source || option.source === source))
      || list.find((option) => option.tag === fileTag);
    if (byTag) return byTag;
    const fallbackSource = source || 'custom';
    return {
      id: fileId || fileTag,
      tag: fileTag,
      name: '',
      source: fallbackSource,
      group: fallbackSource === 'anatomage' ? ANATOMAGE_GROUP : CUSTOM_GROUP,
    };
  }
  return null;
}
